import React, { useState } from 'react';
import { Plus, Coins, Trash2, Film } from 'lucide-react';
import RechargeCreditModal from '../../features/billing/RechargeCreditModal';
import { getVideoExpiryInfo } from '../../utils/videoExpiry';

export function Sidebar({
  credits = 124,
  projects = [],
  activeProjectId,
  onSelectProject,
  onNewProject,
  onDeleteProject,
  onAddCredits,
  onOpenRecharge
}) {
  const [internalModalOpen, setInternalModalOpen] = useState(false);
  const isRechargeModalOpen = internalModalOpen;

  const handleOpenRecharge = () => {
    if (onOpenRecharge) {
      onOpenRecharge();
    } else {
      setInternalModalOpen(true);
    }
  };

  return (
    <aside
      style={{
        width: '280px',
        minWidth: '280px',
        height: '100vh',
        position: 'sticky',
        top: 0,
        backgroundColor: 'var(--bg-sidebar)',
        color: 'var(--text-sidebar)',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid var(--border-sidebar)',
        padding: '20px 16px',
        gap: '20px',
        overflowY: 'auto'
      }}
    >
      {/* Brand Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '4px 6px' }}>
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: 'var(--primary-gradient)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(234, 88, 12, 0.4)'
          }}
        >
          <Film size={20} color="#FFFFFF" />
        </div>
        <div>
          <h2 style={{ fontSize: '16px', fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            Compare<span style={{ color: '#FF6B35' }}>Video</span>
          </h2>
          <span style={{ fontSize: '11px', color: 'var(--text-sidebar-dim)' }}>
            Studio Dựng Video So Sánh 9:16
          </span>
        </div>
      </div>

      {/* Credit Wallet Widget (Matches Image 1) */}
      <div
        style={{
          background: 'var(--bg-sidebar-card)',
          borderRadius: 'var(--radius-md)',
          padding: '16px',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}
      >
        <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--text-sidebar-dim)', textTransform: 'uppercase' }}>
          SỐ DƯ HIỆN TẠI
        </span>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
          <span style={{ fontSize: '24px', fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.02em' }}>
            {credits}
          </span>
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#FF6B35' }}>
            credit
          </span>
        </div>
        <p style={{ fontSize: '12px', color: 'var(--text-sidebar-dim)', lineHeight: 1.3 }}>
          Đủ cho ~{Math.floor(credits / 10)}-{Math.floor(credits / 8)} video so sánh ngắn.
        </p>
        <button
          type="button"
          onClick={handleOpenRecharge}
          style={{
            marginTop: '4px',
            padding: '8px 12px',
            background: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: 'var(--radius-sm)',
            color: '#FFFFFF',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            transition: 'all var(--transition-fast)'
          }}
        >
          <Coins size={14} color="#FF6B35" />
          <span>Nạp thêm credit</span>
        </button>
      </div>

      {/* New Project Button */}
      <button
        type="button"
        onClick={onNewProject}
        style={{
          padding: '12px 16px',
          background: 'var(--primary-gradient)',
          border: 'none',
          borderRadius: 'var(--radius-md)',
          color: '#FFFFFF',
          fontSize: '14px',
          fontWeight: 700,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          boxShadow: '0 4px 15px rgba(234, 88, 12, 0.3)',
          transition: 'all var(--transition-fast)'
        }}
      >
        <Plus size={18} strokeWidth={2.5} />
        <span>Tạo video mới</span>
      </button>

      {/* Recent Projects List (Matches Image 1) */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px' }}>
          <span style={{ fontSize: '13px', fontWeight: 700, color: '#FFFFFF' }}>
            Dự án gần đây
          </span>
          <span style={{ fontSize: '11px', color: 'var(--text-sidebar-dim)', cursor: 'pointer' }}>
            Xem tất cả
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {projects.length === 0 ? (
            <span style={{ fontSize: '12px', color: 'var(--text-sidebar-dim)', textAlign: 'center', padding: '20px 0' }}>
              Chưa có dự án nào.
            </span>
          ) : (
            projects.map((proj) => {
              const isSelected = activeProjectId === proj.id;
              const hasVideo = Boolean(proj.videoUrl || proj.latestRenderJobId);
              const expiry = hasVideo ? getVideoExpiryInfo(proj) : null;

              return (
                <div
                  key={proj.id}
                  onClick={() => onSelectProject(proj.id)}
                  style={{
                    padding: '12px 14px',
                    borderRadius: 'var(--radius-md)',
                    background: isSelected ? 'rgba(234, 88, 12, 0.15)' : 'var(--bg-sidebar-card)',
                    border: `1px solid ${isSelected ? 'var(--primary)' : 'rgba(255, 255, 255, 0.05)'}`,
                    cursor: 'pointer',
                    transition: 'all var(--transition-fast)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}
                >
                  {/* Hàng hạn lưu trữ đứng riêng phía trên: để chung hàng với tiêu đề thì badge
                      chiếm mất bề ngang và ép tên dự án dài xuống thành cột hẹp nhiều dòng. */}
                  {hasVideo && (
                    <div style={{ display: 'flex' }}>
                      {expiry?.isExpired ? (
                        <span
                          style={{
                            fontSize: '9px',
                            fontWeight: 700,
                            padding: '2px 7px',
                            borderRadius: '4px',
                            background: 'rgba(239, 68, 68, 0.2)',
                            color: '#F87171',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Hết hạn
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: '9px',
                            fontWeight: 600,
                            padding: '2px 7px',
                            borderRadius: '4px',
                            background: expiry?.isUrgent ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.15)',
                            color: expiry?.isUrgent ? '#F87171' : '#34D399',
                            whiteSpace: 'nowrap'
                          }}
                          title={expiry?.formattedExpiry ? `Hết hạn lúc: ${expiry.formattedExpiry}` : 'Lưu trữ 3 ngày'}
                        >
                          {expiry?.remainingText ? `Hạn: ${expiry.remainingText}` : 'Video 3 ngày'}
                        </span>
                      )}
                    </div>
                  )}

                  <h4 style={{ fontSize: '13px', fontWeight: 700, color: '#FFFFFF', lineHeight: 1.35 }}>
                    {proj.name}
                  </h4>
                  <span style={{ fontSize: '11px', color: '#94A3B8' }}>
                    {proj.tone || 'MVP render thật'}
                  </span>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', fontSize: '10px', color: 'var(--text-sidebar-dim)' }}>
                    <span>{proj.voiceId ? 'Có lồng tiếng' : 'Chưa audio'} • {proj.targetDuration || 30}s</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ color: '#FF6B35', fontWeight: 600 }}>12 credit</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Xóa dự án "${proj.name}"?`)) {
                            onDeleteProject(proj.id);
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--text-sidebar-dim)',
                          cursor: 'pointer',
                          padding: '2px',
                          display: 'flex'
                        }}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Recharge Modal */}
      {!onOpenRecharge && (
        <RechargeCreditModal
          isOpen={internalModalOpen}
          onClose={() => setInternalModalOpen(false)}
          currentCredits={credits}
          onAddCredits={onAddCredits}
        />
      )}
    </aside>
  );
}

export default Sidebar;
